import { Card, CardContent, FormControl, Grid, IconButton, InputAdornment, MenuItem, Snackbar, Tooltip } from '@material-ui/core';
import React, { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FileCopyOutlined, SwapVertSharp } from '@material-ui/icons';
import Alert, { Color } from '@material-ui/lab/Alert';
import { FormikProvider, useFormik } from 'formik';
import debounce from 'lodash/debounce';
import CustomTextField from 'src/components/CustomTextField/custom-textfield.component';
import { AreaUnitEnum, ConvertTypeEnum, UnitConverterLengthUnitEnum, VolumeUnitEnum, WeightUnitEnum } from 'src/shared/enum';
import { GetUnitConverterFormInitialValues, GetUnitConverterFormValidationSchema, IUnitConverterRequestResponse } from 'src/models/unitConverter.model';
import { ToolsService } from 'src/services/tools.service';
import { Constant, unitDisplayMapping } from 'src/shared/constants';

const UnitConverterTool: React.FC = () => {

	const { t } = useTranslation();

	// useStates are created here.
	const [selectedConvertTypeUnits, setSelectedConvertTypeUnits] = useState<string[]>([]);
	const [snackbarState, setSnackbarState] = useState<boolean>(false);
	const [snackbarMessage, setSnackbarMessage] = useState<string>('');
	const [severity, setSeverity] = useState<Color>(Constant.colorError);

	// Initialize formik
	const formik = useFormik({
		initialValues: GetUnitConverterFormInitialValues(),
		validationSchema: GetUnitConverterFormValidationSchema(t),
		onSubmit: () => { }
	});

	useEffect(() => {
		handleConvertTypeChange(formik.values.convertType)
	}, [formik.values.convertType])

	/**
	 * Function to handle copying text to clipboard
	 * @param textToCopy 
	 */
	const handleCopy = async (textToCopy: string) => {
		try {
			await navigator.clipboard.writeText(textToCopy);
			setSnackbarMessage(t('label-value-copied'));
			setSeverity(Constant.colorInfo);
			setSnackbarState(true);
		} catch (err) { }
	};

	/**
	 * @param convertType To get Units based on value of current ConvertType field
	 * @returns 
	 */
	const getConvertTypePossibleUnits = (convertType: ConvertTypeEnum) => {
		switch (convertType) {
			case ConvertTypeEnum.Length:
				return Object.values(UnitConverterLengthUnitEnum);
			case ConvertTypeEnum.Weight:
				return Object.values(WeightUnitEnum);
			case ConvertTypeEnum.Area:
				return Object.values(AreaUnitEnum);
			case ConvertTypeEnum.Volume:
				return Object.values(VolumeUnitEnum);
			default:
				return [];
		}
	};

	/**
	 * To filter units by removing the already selected unit in source/final field
	 * @param isFinal 
	 * @returns 
	 */
	const getFilteredUnits = (isFinal: boolean = false) => {
		if (isFinal) {
			return selectedConvertTypeUnits.filter(unit => unit !== (isFinal ? formik.values.sourceUnit : formik.values.finalUnit));
		}
		else {
			return selectedConvertTypeUnits.filter(unit => unit !== formik.values.finalUnit);
		}
	}

	/**
	 * To fetch converted values from API
	 * @param values 
	 * @param isFinalValue 
	 */
	const getConvertedValues = async (convertType: ConvertTypeEnum, values: IUnitConverterRequestResponse, isFinalValue: boolean = false) => {
		try {
			//isFinal will be true if change in final value field is done 
			const params: IUnitConverterRequestResponse = isFinalValue
				? {
					sourceValue: values.finalValue ?? 0,
					sourceUnit: values.finalUnit,
					finalUnit: values.sourceUnit,
				}
				: {
					sourceValue: values.sourceValue,
					sourceUnit: values.sourceUnit,
					finalUnit: values.finalUnit,
				};

			const response = await ToolsService.getConvertedUnit(convertType, params);
			const data = response.data;

			if (isFinalValue) {
				formik.setFieldValue('sourceValue', data.finalValue);
				formik.setFieldValue('finalValue', data.sourceValue);
			} else {
				formik.setFieldValue('sourceValue', data.sourceValue);
				formik.setFieldValue('finalValue', data.finalValue);
			}
		} catch (err: any) {
			setSnackbarMessage(err);
			setSeverity(Constant.colorError);
			setSnackbarState(true);
		}
	};

	// Debounce the API call to avoid too many requests as user types 
	const debouncedGetConvertedValues = useCallback(
		debounce(getConvertedValues, 500),
		[]
	);

	/**
	 * Function to handle convertType field change and trigger debounced function
	 * @param value 
	 */
	const handleConvertTypeChange = (value: ConvertTypeEnum) => {
		const units = getConvertTypePossibleUnits(value);
		setSelectedConvertTypeUnits(units)
		formik.setValues({
			...formik.values,
			convertType: value,
			sourceUnit: units[0],
			finalUnit: units[1]
		})
		// If source field is empty at Convert Type change
		if (formik.values.sourceValue.toString() === '') {
			return
		}
		const values = { ...formik.values, 'covertType': value, 'sourceUnit': units[0], 'finalUnit': units[1] };
		debouncedGetConvertedValues(values.convertType, values, false);
	};

	/**
	 * Function to handle source field or final field value change and trigger debounced function
	 * @param fieldName 
	 * @param value 
	 * @returns 
	 */
	const handleInputChange = (fieldName: string, value: any) => {

		 // Set finalValue and sourceValue to empty if input value is empty
		if (value === '') {
			formik.setFieldValue('finalValue', '', true);
			formik.setFieldValue('sourceValue', '', true);
			debouncedGetConvertedValues.cancel();
			return;
		}
         
		// Set fields touched to show validation error messages 
		 formik.submitForm()

		if (!formik.isValid) {
			return;	
		}
		const values = { ...formik.values, [fieldName]: value };
		debouncedGetConvertedValues(values.convertType, values, fieldName === 'finalValue');
	};

	/**
	 * Function to swap source field value and final field value on click of SwapVertiIcon 
	 */
	const onSwapIconClick = () => {
		formik.setValues({
			...formik.values,
			sourceValue: formik.values.finalValue,
			finalValue: formik.values.sourceValue,
			sourceUnit: formik.values.finalUnit,
			finalUnit: formik.values.sourceUnit
		})
	};

	return (
		<>
			<Card>
				<CardContent>
					<FormikProvider value={formik}>
						{/* Grid for selecting conversion type */}
						<Grid container direction='column'>
							<Grid item sm={8} xl={6}>
								<FormControl fullWidth variant='standard'>
									<CustomTextField
										name={'convertType'}
										label={t('label-convert-type')}
										placeholder={''}
										formik={formik}
										select
										children={Object.keys(ConvertTypeEnum).map((type, index) => (
											<MenuItem key={index} value={type}>
												{type}
											</MenuItem>
										))}
									/>
								</FormControl>
							</Grid>
							<Grid>
								{/* Grid for entering source value and selecting source unit */}
								<Grid item sm={8} xl={6} className='mt-20'>
									<FormControl fullWidth variant='standard'>
										<CustomTextField
											type='number'
											name={'sourceValue'}
											placeholder={''}
											label={t('label-from')}
											formik={formik}
											onChange={(e: any) => handleInputChange('sourceValue', e)}
											InputProps={{
												inputProps: { min: 0 },
												endAdornment: (
													<InputAdornment position='end'>
														<CustomTextField
															name={'sourceUnit'}
															placeholder={''}
															label={t('')}
															formik={formik}
															select
															noUnderline
															onChange={(e: any) => handleInputChange('sourceUnit', e)}
															children={getFilteredUnits().map((unit, index) => (
																<MenuItem key={index} value={unit}>
																	{unitDisplayMapping[unit]}
																</MenuItem>
															))}
														/>
														<Tooltip title='Copy' placement='top' arrow={true}>
															<IconButton
																onClick={() => handleCopy(formik.values.sourceValue.toString())}
																size='small'
															>
																<FileCopyOutlined className='font17px' />
															</IconButton>
														</Tooltip>
													</InputAdornment>
												),
											}}
										/>
									</FormControl>
								</Grid>

								{/* SwapVertIcon with Swapunit function */}
								<Grid item sm={8} xl={6} container justifyContent='center' className='mt-20'>
									<IconButton onClick={onSwapIconClick}>
										<SwapVertSharp color='action' style={{ cursor: 'pointer' }} />
									</IconButton>
								</Grid>

								{/* Grid for entering final value and selecting final unit */}
								<Grid item sm={8} xl={6} >
									<FormControl fullWidth variant='standard'>
										<CustomTextField
											type='number'
											name={'finalValue'}
											placeholder={''}
											label={t('label-to')}
											formik={formik}
											onChange={(e: any) => handleInputChange('finalValue', e)}
											InputProps={{
												inputProps: { min: 0 },
												endAdornment: (
													<InputAdornment position='end'>
														<CustomTextField
															name={'finalUnit'}
															placeholder={''}
															label={t('')}
															formik={formik}
															select
															noUnderline
															onChange={(e: any) => handleInputChange('finalUnit', e)}
															children={getFilteredUnits(true).map((unit, index) => (
																<MenuItem key={index} value={unit}>
																	{unitDisplayMapping[unit]}
																</MenuItem>
															))}
														/>
														<Tooltip title='Copy' placement='top' arrow={true}>
															<IconButton
																onClick={() => {
																	const finalValue = formik.values.finalValue ?? '';
																	handleCopy(finalValue.toString());
																}}
																size='small'
															>
																<FileCopyOutlined className='font17px' />
															</IconButton>
														</Tooltip>
													</InputAdornment>
												),
											}}
										/>
									</FormControl>
								</Grid>
							</Grid>
						</Grid>
					</FormikProvider>
				</CardContent>
				{/* Snackbar for showing messages */}
			</Card>
			<Snackbar
				open={snackbarState}
				autoHideDuration={6000}
				onClose={() => setSnackbarState(false)}
			>
				<Alert severity={severity}>{snackbarMessage}</Alert>
			</Snackbar>
		</>
	);
};

export default UnitConverterTool;
